import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Loader2, Rocket, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SOURCE_LABEL = {
  paywall: 'Paywall (locked feature)',
  pricing: 'Pricing page',
  banner: 'Trial banner',
  other: 'Other',
};

/**
 * TrialFunnelCard — SuperAdmin view of the trial funnel
 * (`GET /api/analytics/trials/summary`): how many trials started from each
 * source (paywall vs pricing) and the trial→paid conversion rate per source.
 */
const TrialFunnelCard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/analytics/trials/summary`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load trial analytics');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const bySource = data?.by_source || {};
  const totalTrials = data?.total_trials || 0;
  const totalConverted = data?.total_converted || 0;
  const overallRate = data?.conversion_rate != null ? Math.round(data.conversion_rate * 100) : 0;

  const rows = Object.entries(bySource)
    .map(([src, v]) => ({
      src,
      label: SOURCE_LABEL[src] || src,
      trials: v.trials || 0,
      converted: v.converted || 0,
      rate: v.conversion_rate != null ? Math.round(v.conversion_rate * 100) : 0,
    }))
    .sort((a, b) => b.trials - a.trials);

  return (
    <Card className="border-2 border-indigo-300/50 dark:border-indigo-700/40" data-testid="trial-funnel-card">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-indigo-700 dark:text-indigo-300">
            <Rocket className="w-5 h-5" /> Trial Funnel — Source & Conversion
          </span>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="trial-funnel-refresh-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {loading && !data && (
          <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        )}
        {error && <p className="text-red-600 italic" data-testid="trial-funnel-error">{error}</p>}
        {data && totalTrials === 0 && (
          <p className="text-slate-500 italic" data-testid="trial-funnel-empty">
            No trials started yet. Once users activate a trial (from the paywall or pricing page), the funnel appears here.
          </p>
        )}
        {data && totalTrials > 0 && (
          <>
            <div className="grid grid-cols-3 gap-3" data-testid="trial-funnel-metrics">
              <div className="rounded-lg bg-muted p-3 text-center">
                <p className="text-2xl font-bold">{totalTrials}</p>
                <p className="text-[11px] uppercase tracking-widest text-slate-500">Trials</p>
              </div>
              <div className="rounded-lg bg-muted p-3 text-center">
                <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{totalConverted}</p>
                <p className="text-[11px] uppercase tracking-widest text-slate-500">Converted</p>
              </div>
              <div className="rounded-lg bg-muted p-3 text-center">
                <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{overallRate}%</p>
                <p className="text-[11px] uppercase tracking-widest text-slate-500">Trial→Paid</p>
              </div>
            </div>
            <div className="space-y-2" data-testid="trial-funnel-sources">
              <p className="text-[10px] uppercase tracking-widest text-slate-400">Which source drives the best conversions</p>
              {rows.map(({ src, label, trials, converted, rate }) => (
                <div key={src} className="space-y-1" data-testid={`trial-funnel-source-${src}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium truncate pr-2">{label}</span>
                    <span className="text-xs text-slate-500 flex-shrink-0">{converted}/{trials} · {rate}%</span>
                  </div>
                  <div className="w-full h-2 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full bg-indigo-500 transition-all" style={{ width: `${rate}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default TrialFunnelCard;
